import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Landmark, AlertTriangle, Search, Download, ArrowUpRight, Wallet, Clock } from "lucide-react";
import { formatCurrency, formatNumber } from "@/lib/constants";

const BUCKET_STYLE = {
  expired: { label: "Expired", cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  "30d": { label: "Within 30 days", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  "60d": { label: "Within 60 days", cls: "bg-yellow-50 text-yellow-700 border-yellow-200", dot: "bg-yellow-400" },
  "90d": { label: "Within 90 days", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  later: { label: "Beyond 90 days", cls: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
};

function formatDaysLeft(days) {
  if (days == null) return "—";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  return `${days}d left`;
}

export default function PbgLedgerPage() {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [bucketFilter, setBucketFilter] = useState("all");

  useEffect(() => {
    api.get("/dashboard/pbg-ledger")
      .then((r) => setLedger(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!ledger) return [];
    let rows = ledger.rows;
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        (r.po_number || "").toLowerCase().includes(q) ||
        (r.buyer_name || "").toLowerCase().includes(q) ||
        (r.department || "").toLowerCase().includes(q) ||
        (r.product_name || "").toLowerCase().includes(q)
      );
    }
    if (bucketFilter !== "all") rows = rows.filter((r) => r.bucket === bucketFilter);
    return rows;
  }, [ledger, search, bucketFilter]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!ledger) return null;

  const exportCsv = () => {
    const headers = ["PO Number", "Buyer", "Department", "Product", "Contract Value", "Est. Frozen Capital", "PBG Expiry", "Pickup Reminder", "Days to Expiry", "Responsible", "Status"];
    const rows = filtered.map((r) => [
      r.po_number, r.buyer_name, r.department, r.product_name,
      r.contract_value, r.estimated_frozen_capital, r.pbg_expiry_date,
      r.pbg_pickup_reminder || "", r.days_to_expiry, r.responsible_person, r.status,
    ]);
    const csv = [headers, ...rows].map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "pbg-ledger.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="pbg-ledger-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold flex items-center gap-2">
            <Landmark className="h-3.5 w-3.5" /> Accounts · Bank Guarantee
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">PBG Recovery Ledger</h1>
          <p className="text-sm text-slate-500 mt-1">
            Frozen capital locked in Performance Bank Guarantees across active POs. Recover before expiry to avoid auto-forfeiture.
          </p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="pbg-export-csv">
          <Download className="h-4 w-4 mr-2" /> Export
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Total PBGs Active" value={formatNumber(ledger.count)} icon={Landmark} accent="slate" testid="pbg-kpi-total" />
        <SummaryCard label="Frozen Capital (est.)" value={formatCurrency(ledger.total_estimated_frozen_capital)} sub="~10% of contract value" icon={Wallet} accent="amber" testid="pbg-kpi-frozen" />
        <SummaryCard label="Expired PBGs" value={ledger.buckets.expired || 0} sub="Urgent — may be forfeited" icon={AlertTriangle} accent="red" testid="pbg-kpi-expired" />
        <SummaryCard label="Expiring in 30 days" value={ledger.buckets["30d"] || 0} sub="Action needed" icon={Clock} accent="amber" testid="pbg-kpi-30d" />
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input placeholder="Search PO number, buyer, product..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pbg-search" />
          </div>
          <Select value={bucketFilter} onValueChange={setBucketFilter}>
            <SelectTrigger className="w-56" data-testid="pbg-bucket-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time Buckets</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="30d">Within 30 days</SelectItem>
              <SelectItem value="60d">Within 60 days</SelectItem>
              <SelectItem value="90d">Within 90 days</SelectItem>
              <SelectItem value="later">Beyond 90 days</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["PO Number", "Buyer / Department", "Product", "Contract Value", "Est. Frozen", "PBG Expiry", "Days Left", "Pickup Reminder", "Actions"].map((h) => (
                  <th key={h} className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-slate-500">
                    No PBGs match this filter. Set <em>PBG Expiry Date</em> on Purchase Orders to see them here.
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const bstyle = BUCKET_STYLE[r.bucket] || BUCKET_STYLE.later;
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`pbg-row-${r.id}`}>
                    <td className="py-3 px-4 font-semibold text-slate-900">{r.po_number || "—"}</td>
                    <td className="py-3 px-4 text-slate-700">
                      <div className="font-medium">{r.buyer_name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.department || "—"}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-700">{r.product_name || "—"}</td>
                    <td className="py-3 px-4 font-mono text-slate-900">{formatCurrency(r.contract_value)}</td>
                    <td className="py-3 px-4 font-mono font-semibold text-amber-700">{formatCurrency(r.estimated_frozen_capital)}</td>
                    <td className="py-3 px-4 text-slate-700">{r.pbg_expiry_date}</td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className={bstyle.cls}>
                        <span className={`h-1.5 w-1.5 rounded-full ${bstyle.dot} mr-1.5`} />
                        {formatDaysLeft(r.days_to_expiry)}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-slate-700">{r.pbg_pickup_reminder || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 px-4">
                      <Link
                        to={`/m/purchase_orders`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                        data-testid={`pbg-open-po-${r.id}`}
                      >
                        Open PO <ArrowUpRight className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="text-xs text-slate-500 mt-2">
        <strong>Note:</strong> Frozen Capital is estimated at 10% of Contract Value — the standard PBG rate in Indian government tenders. Actual PBG amounts may vary; edit the PO record to refine.
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, icon: Icon, accent = "slate", testid }) {
  const bg = {
    slate: "bg-slate-50 text-slate-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    emerald: "bg-emerald-50 text-emerald-600",
  }[accent] || "bg-slate-50 text-slate-600";
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
            <div className="text-2xl font-bold text-slate-900 mt-2 font-[Manrope]">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
          </div>
          <div className={`h-10 w-10 rounded-lg ${bg} flex items-center justify-center`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
